
import { useState, useEffect, createContext, useContext, ReactNode } from "react";

interface Notification {
  id: string;
  type: "info" | "success" | "warning" | "error" | "operation" | "document" | "payment";
  title: string;
  message: string;
  time: Date;
  read: boolean;
  actionUrl?: string;
  operationId?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, "id" | "time" | "read">) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotification: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
}

// Notificaciones iniciales de demostración
const initialNotifications: Notification[] = [
  {
    id: "1",
    type: "operation",
    title: "Operación actualizada",
    message: "OP-2024-0156 ha llegado a puerto de destino Veracruz",
    time: new Date(Date.now() - 1000 * 60 * 5),
    read: false,
    operationId: "OP-2024-0156",
    actionUrl: "/portal/operations"
  },
  {
    id: "2",
    type: "document",
    title: "Documento disponible",
    message: "BL de la operación OP-2024-0155 está listo para descarga",
    time: new Date(Date.now() - 1000 * 60 * 30),
    read: false,
    operationId: "OP-2024-0155",
    actionUrl: "/portal/documents"
  },
  {
    id: "3",
    type: "warning",
    title: "Documento pendiente",
    message: "Falta certificado de origen para OP-2024-0156",
    time: new Date(Date.now() - 1000 * 60 * 60 * 2),
    read: false,
    operationId: "OP-2024-0156"
  },
  {
    id: "4",
    type: "payment",
    title: "Pago confirmado",
    message: "Se aplicó pago de $15,680.00 a su cuenta",
    time: new Date(Date.now() - 1000 * 60 * 60 * 24),
    read: true,
    actionUrl: "/portal/payments"
  },
  {
    id: "5",
    type: "error",
    title: "Factura vencida",
    message: "FC-2024-0078 venció hace 5 días - Monto: $8,450.00",
    time: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5),
    read: true,
    actionUrl: "/portal/invoices"
  },
  {
    id: "6",
    type: "success",
    title: "Operación finalizada",
    message: "OP-2024-0153 ha sido completada exitosamente",
    time: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2),
    read: true,
    operationId: "OP-2024-0153"
  }
];

// Simulador de notificaciones en tiempo real
const simulatedNotifications = [
  {
    type: "operation" as const,
    title: "Cambio de estado",
    message: "OP-2024-0155 pasó a estado: Despacho Aduanal",
    operationId: "OP-2024-0155"
  },
  {
    type: "document" as const,
    title: "Nuevo documento",
    message: "DUCA disponible para OP-2024-0156",
    operationId: "OP-2024-0156",
    actionUrl: "/portal/documents"
  },
  {
    type: "info" as const,
    title: "Mensaje de su gestor",
    message: "María López le ha enviado un mensaje sobre OP-2024-0156",
    actionUrl: "/portal/messages"
  },
  {
    type: "payment" as const,
    title: "Nueva factura",
    message: "Se ha generado la factura FC-2024-0089 por $12,350.00",
    actionUrl: "/portal/invoices"
  },
  {
    type: "warning" as const,
    title: "Próximo vencimiento",
    message: "Factura FC-2024-0082 vence en 3 días",
    actionUrl: "/portal/invoices"
  }
];

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>(initialNotifications);

  const unreadCount = notifications.filter(n => !n.read).length;

  const addNotification = (notification: Omit<Notification, "id" | "time" | "read">) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      time: new Date(),
      read: false
    };
    setNotifications(prev => [newNotification, ...prev]);
  };

  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const clearNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  // Simulador de notificaciones en tiempo real (cada 45-90 segundos)
  useEffect(() => {
    const interval = setInterval(() => {
      const randomNotification = simulatedNotifications[Math.floor(Math.random() * simulatedNotifications.length)];
      addNotification(randomNotification);
    }, Math.random() * 45000 + 45000);

    return () => clearInterval(interval);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearNotification,
        clearAll
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

// Componente del Panel de Notificaciones
export function NotificationPanel({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotification, clearAll } = useNotifications();
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [showSettings, setShowSettings] = useState(false);

  const filteredNotifications = filter === "unread" 
    ? notifications.filter(n => !n.read) 
    : notifications;

  const getIcon = (type: string) => {
    switch (type) {
      case "operation": return "ri-ship-line";
      case "document": return "ri-file-text-line";
      case "payment": return "ri-money-dollar-circle-line";
      case "success": return "ri-checkbox-circle-line";
      case "warning": return "ri-alert-line";
      case "error": return "ri-error-warning-line";
      case "info": return "ri-information-line";
      default: return "ri-notification-line";
    }
  };

  const getColor = (type: string) => {
    switch (type) {
      case "operation": return "#2F80ED";
      case "document": return "#8B5CF6";
      case "payment": return "#10B981";
      case "success": return "#10B981";
      case "warning": return "#EAB308";
      case "error": return "#EF4444";
      case "info": return "#06B6D4";
      default: return "#6B7280";
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Ahora";
    if (minutes < 60) return `Hace ${minutes} min`;
    if (hours < 24) return `Hace ${hours}h`;
    if (days === 1) return "Ayer";
    return `Hace ${days} días`;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-40" onClick={onClose}></div>
      
      {/* Panel */}
      <div className="fixed right-4 top-16 w-96 max-h-[calc(100vh-100px)] bg-white rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-[#114B7A] to-[#2F80ED]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <i className="ri-notification-3-line text-xl text-white"></i>
              <h3 className="text-lg font-bold text-white">Notificaciones</h3>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-bold bg-white text-[#2F80ED] rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSettings(true)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors cursor-pointer"
              >
                <i className="ri-settings-3-line text-white"></i>
              </button>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-white text-xl"></i>
              </button>
            </div>
          </div>
          
          {/* Filtros */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                filter === "all" ? "bg-white text-[#2F80ED]" : "bg-white/20 text-white hover:bg-white/30"
              }`}
            >
              Todas
            </button>
            <button
              onClick={() => setFilter("unread")}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                filter === "unread" ? "bg-white text-[#2F80ED]" : "bg-white/20 text-white hover:bg-white/30"
              }`}
            >
              No leídas ({unreadCount})
            </button>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="ml-auto text-xs text-white/80 hover:text-white cursor-pointer whitespace-nowrap"
              >
                Marcar todas leídas
              </button>
            )}
          </div>
        </div>

        {/* Lista de Notificaciones */}
        <div className="flex-1 overflow-y-auto">
          {filteredNotifications.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-gray-100">
                <i className="ri-notification-off-line text-3xl text-gray-400"></i>
              </div>
              <p className="text-sm text-gray-500">No hay notificaciones</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                    !notification.read ? "bg-[#EAF2F8]/50" : ""
                  }`}
                  onClick={() => {
                    markAsRead(notification.id);
                    if (notification.actionUrl) {
                      window.location.href = notification.actionUrl;
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0"
                      style={{ backgroundColor: `${getColor(notification.type)}15` }}
                    >
                      <i
                        className={`${getIcon(notification.type)} text-xl`}
                        style={{ color: getColor(notification.type) }}
                      ></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!notification.read ? "font-semibold" : "font-medium"} text-[#0A2A43]`}>
                          {notification.title}
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            clearNotification(notification.id);
                          }}
                          className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-close-line text-gray-400"></i>
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notification.message}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-gray-400">{formatTime(notification.time)}</span>
                        {!notification.read && (
                          <span className="w-2 h-2 rounded-full bg-[#2F80ED]"></span>
                        )}
                        {notification.operationId && (
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                            {notification.operationId}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
            <a
              href="/portal"
              className="text-xs text-[#2F80ED] hover:underline cursor-pointer"
            >
              Ver historial completo
            </a>
            <button
              onClick={clearAll}
              className="text-xs text-gray-500 hover:text-[#EF4444] cursor-pointer whitespace-nowrap"
            >
              Limpiar todas
            </button>
          </div>
        )}
      </div>

      {/* Modal de Configuración */}
      {showSettings && (
        <NotificationSettings onClose={() => setShowSettings(false)} />
      )}
    </>
  );
}

// Componente de Configuración de Notificaciones
function NotificationSettings({ onClose }: { onClose: () => void }) {
  const [settings, setSettings] = useState({
    pushEnabled: true,
    emailEnabled: true,
    soundEnabled: true,
    operationUpdates: true,
    documentAlerts: true,
    paymentAlerts: true,
    invoiceReminders: true,
    messageNotifications: true,
    weeklyReport: false
  });

  const toggleSetting = (key: keyof typeof settings) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-xl w-full max-w-md mx-4 max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#EAF2F8]">
              <i className="ri-settings-3-line text-xl text-[#2F80ED]"></i>
            </div>
            <h2 className="text-lg font-bold text-[#0A2A43]">Configuración de Notificaciones</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-500"></i>
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
          {/* Canales de Notificación */}
          <div>
            <h3 className="text-sm font-semibold text-[#0A2A43] mb-3">Canales de Notificación</h3>
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                <div className="flex items-center gap-3">
                  <i className="ri-notification-3-line text-lg text-[#2F80ED]"></i>
                  <div>
                    <p className="text-sm font-medium text-[#0A2A43]">Notificaciones Push</p>
                    <p className="text-xs text-gray-500">Alertas en tiempo real en el navegador</p>
                  </div>
                </div>
                <div
                  onClick={() => toggleSetting("pushEnabled")}
                  className={`w-11 h-6 rounded-full transition-colors cursor-pointer ${
                    settings.pushEnabled ? "bg-[#2F80ED]" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform mt-0.5 ${
                      settings.pushEnabled ? "translate-x-5.5 ml-0.5" : "translate-x-0.5"
                    }`}
                  ></div>
                </div>
              </label>

              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line text-lg text-[#8B5CF6]"></i>
                  <div>
                    <p className="text-sm font-medium text-[#0A2A43]">Correo Electrónico</p>
                    <p className="text-xs text-gray-500">Resumen de alertas importantes</p>
                  </div>
                </div>
                <div
                  onClick={() => toggleSetting("emailEnabled")}
                  className={`w-11 h-6 rounded-full transition-colors cursor-pointer ${
                    settings.emailEnabled ? "bg-[#2F80ED]" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform mt-0.5 ${
                      settings.emailEnabled ? "translate-x-5.5 ml-0.5" : "translate-x-0.5"
                    }`}
                  ></div>
                </div>
              </label>

              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                <div className="flex items-center gap-3">
                  <i className="ri-volume-up-line text-lg text-[#10B981]"></i>
                  <div>
                    <p className="text-sm font-medium text-[#0A2A43]">Sonido</p>
                    <p className="text-xs text-gray-500">Alerta sonora para nuevas notificaciones</p>
                  </div>
                </div>
                <div
                  onClick={() => toggleSetting("soundEnabled")}
                  className={`w-11 h-6 rounded-full transition-colors cursor-pointer ${
                    settings.soundEnabled ? "bg-[#2F80ED]" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform mt-0.5 ${
                      settings.soundEnabled ? "translate-x-5.5 ml-0.5" : "translate-x-0.5"
                    }`}
                  ></div>
                </div>
              </label>
            </div>
          </div>

          {/* Tipos de Alertas */}
          <div>
            <h3 className="text-sm font-semibold text-[#0A2A43] mb-3">Tipos de Alertas</h3>
            <div className="space-y-3">
              {[
                { key: "operationUpdates", icon: "ri-ship-line", color: "#2F80ED", label: "Actualizaciones de Operaciones", desc: "Cambios de estado, llegadas, despachos" },
                { key: "documentAlerts", icon: "ri-file-text-line", color: "#8B5CF6", label: "Alertas de Documentos", desc: "Documentos disponibles o pendientes" },
                { key: "paymentAlerts", icon: "ri-money-dollar-circle-line", color: "#10B981", label: "Alertas de Pagos", desc: "Confirmaciones y recordatorios" },
                { key: "invoiceReminders", icon: "ri-bill-line", color: "#EAB308", label: "Recordatorios de Facturas", desc: "Vencimientos próximos" },
                { key: "messageNotifications", icon: "ri-message-2-line", color: "#06B6D4", label: "Mensajes", desc: "Nuevos mensajes de gestores" }
              ].map((item) => (
                <label key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                  <div className="flex items-center gap-3">
                    <i className={`${item.icon} text-lg`} style={{ color: item.color }}></i>
                    <div>
                      <p className="text-sm font-medium text-[#0A2A43]">{item.label}</p>
                      <p className="text-xs text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                  <div
                    onClick={() => toggleSetting(item.key as keyof typeof settings)}
                    className={`w-11 h-6 rounded-full transition-colors cursor-pointer ${
                      settings[item.key as keyof typeof settings] ? "bg-[#2F80ED]" : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform mt-0.5 ${
                        settings[item.key as keyof typeof settings] ? "translate-x-5.5 ml-0.5" : "translate-x-0.5"
                      }`}
                    ></div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Reportes */}
          <div>
            <h3 className="text-sm font-semibold text-[#0A2A43] mb-3">Reportes Automáticos</h3>
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
              <div className="flex items-center gap-3">
                <i className="ri-calendar-check-line text-lg text-[#EF4444]"></i>
                <div>
                  <p className="text-sm font-medium text-[#0A2A43]">Reporte Semanal</p>
                  <p className="text-xs text-gray-500">Resumen de actividad cada lunes</p>
                </div>
              </div>
              <div
                onClick={() => toggleSetting("weeklyReport")}
                className={`w-11 h-6 rounded-full transition-colors cursor-pointer ${
                  settings.weeklyReport ? "bg-[#2F80ED]" : "bg-gray-300"
                }`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform mt-0.5 ${
                    settings.weeklyReport ? "translate-x-5.5 ml-0.5" : "translate-x-0.5"
                  }`}
                ></div>
              </div>
            </label>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-lg hover:opacity-90 cursor-pointer whitespace-nowrap"
          >
            Guardar Cambios
          </button>
        </div>
      </div>
    </div>
  );
}

// Componente Toast para notificaciones en tiempo real
export function NotificationToast() {
  const { notifications } = useNotifications();
  const [visibleToasts, setVisibleToasts] = useState<Notification[]>([]);

  useEffect(() => {
    if (notifications.length > 0) {
      const latestNotification = notifications[0];
      const isNew = Date.now() - latestNotification.time.getTime() < 2000;
      
      if (isNew && !latestNotification.read) {
        setVisibleToasts(prev => {
          if (prev.find(t => t.id === latestNotification.id)) return prev;
          return [latestNotification, ...prev].slice(0, 3);
        });

        // Auto-remove after 5 seconds
        setTimeout(() => {
          setVisibleToasts(prev => prev.filter(t => t.id !== latestNotification.id));
        }, 5000);
      }
    }
  }, [notifications]);

  const getIcon = (type: string) => {
    switch (type) {
      case "operation": return "ri-ship-line";
      case "document": return "ri-file-text-line";
      case "payment": return "ri-money-dollar-circle-line";
      case "success": return "ri-checkbox-circle-line";
      case "warning": return "ri-alert-line";
      case "error": return "ri-error-warning-line";
      case "info": return "ri-information-line";
      default: return "ri-notification-line";
    }
  };

  const getColor = (type: string) => {
    switch (type) {
      case "operation": return "#2F80ED";
      case "document": return "#8B5CF6";
      case "payment": return "#10B981";
      case "success": return "#10B981";
      case "warning": return "#EAB308";
      case "error": return "#EF4444";
      case "info": return "#06B6D4";
      default: return "#6B7280";
    }
  };

  const removeToast = (id: string) => {
    setVisibleToasts(prev => prev.filter(t => t.id !== id));
  };

  if (visibleToasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-3">
      {visibleToasts.map((toast, index) => (
        <div
          key={toast.id}
          className="bg-white rounded-lg shadow-2xl border border-gray-100 p-4 w-80 animate-slide-in"
          style={{
            animation: "slideIn 0.3s ease-out",
            transform: `translateY(${index * -10}px)`
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0"
              style={{ backgroundColor: `${getColor(toast.type)}15` }}
            >
              <i
                className={`${getIcon(toast.type)} text-xl`}
                style={{ color: getColor(toast.type) }}
              ></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-[#0A2A43]">{toast.title}</p>
                <button
                  onClick={() => removeToast(toast.id)}
                  className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-gray-400 text-sm"></i>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{toast.message}</p>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-3 h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                backgroundColor: getColor(toast.type),
                animation: "shrink 5s linear forwards"
              }}
            ></div>
          </div>
        </div>
      ))}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
}
